"use client";

import { useState } from "react";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { writeToClipboard } from "@/lib/clipboard";
import { countGraphemes, reverseGraphemes } from "@/lib/tool-kit/text/reverse";

const RE_SPLIT_WORDS = /(\s+)/;
const RE_WORD_SPLIT = /\s+/;

function reverseWords(text: string): string {
  const parts = text.split(RE_SPLIT_WORDS);
  const words: string[] = [];
  const spaces: string[] = [];
  for (const part of parts) {
    if (part.trim().length === 0) {
      spaces.push(part);
    } else {
      words.push(part);
    }
  }
  words.reverse();
  // Reassemble with original spacing
  let result = "";
  let wIdx = 0;
  let sIdx = 0;
  for (const part of parts) {
    if (part.trim().length === 0) {
      result += spaces[sIdx];
      sIdx++;
    } else {
      result += words[wIdx];
      wIdx++;
    }
  }
  return result;
}

function reverseLines(text: string): string {
  return text.split("\n").reverse().join("\n");
}

function StatCard({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="rounded-lg border border-border bg-card p-4 text-center">
      <p className="font-semibold text-2xl text-foreground">{value}</p>
      <p className="mt-1 text-muted-foreground text-sm">{label}</p>
    </div>
  );
}

function OutputBlock({
  copiedLabel,
  copyLabel,
  label,
  text,
}: {
  copiedLabel: string;
  copyLabel: string;
  label: string;
  text: string;
}) {
  const [copied, setCopied] = useState(false);

  async function handleCopy() {
    if (!(await writeToClipboard(text))) {
      return;
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <p className="font-medium text-foreground text-sm">{label}</p>
        <button
          className="rounded-md border border-border px-3 py-1 text-muted-foreground text-xs transition-colors hover:bg-accent hover:text-foreground"
          onClick={handleCopy}
          type="button"
        >
          {copied ? copiedLabel : copyLabel}
        </button>
      </div>
      <div className="rounded-lg border border-border bg-muted/30 p-4">
        <p className="whitespace-pre-wrap break-all text-foreground text-sm leading-relaxed">
          {text}
        </p>
      </div>
    </div>
  );
}

/** English strings. A translated content file overrides these through `ui`. */
export const BACKWARDS_TEXT_GENERATOR_STRINGS = {
  textToReverse: "Text to reverse",
  pasteOrTypeYourText: "Paste or type your text here...",
  characters: "Characters",
  words: "Words",
  lines: "Lines",
  reversedCharacters: "Reversed Characters",
  reversedWordOrder: "Reversed Word Order",
  reversedLineOrder: "Reversed Line Order",
  copyToClipboard: "Copy to Clipboard",
  copied: "Copied!",
} as const;

export default function BackwardsTextGeneratorWidget() {
  const [text, setText] = useState("");

  const hasText = text.trim().length > 0;
  const charReversed = hasText ? reverseGraphemes(text) : "";
  const wordReversed = hasText ? reverseWords(text) : "";
  const lineReversed = hasText ? reverseLines(text) : "";
  const charCount = countGraphemes(text);
  const wordCount = hasText ? text.trim().split(RE_WORD_SPLIT).length : 0;
  const lineCount = hasText ? text.split("\n").length : 0;

  const strings = useToolUi(BACKWARDS_TEXT_GENERATOR_STRINGS);

  const fmt = useToolNumber();

  return (
    <>
      {/* Input */}
      <textarea
        aria-label={strings.textToReverse}
        className="min-h-[200px] w-full resize-y rounded-lg border border-border bg-card p-4 text-foreground text-sm leading-relaxed placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
        onChange={(e) => setText(e.target.value)}
        placeholder={strings.pasteOrTypeYourText}
        value={text}
      />
      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        <StatCard label={strings.characters} value={fmt(charCount)} />
        <StatCard label={strings.words} value={fmt(wordCount)} />
        <StatCard label={strings.lines} value={fmt(lineCount)} />
      </div>
      {/* Reversed outputs */}
      {hasText && (
        <div className="space-y-6">
          <OutputBlock
            copiedLabel={strings.copied}
            copyLabel={strings.copyToClipboard}
            label={strings.reversedCharacters}
            text={charReversed}
          />
          <OutputBlock
            copiedLabel={strings.copied}
            copyLabel={strings.copyToClipboard}
            label={strings.reversedWordOrder}
            text={wordReversed}
          />
          {lineCount > 1 && (
            <OutputBlock
              copiedLabel={strings.copied}
              copyLabel={strings.copyToClipboard}
              label={strings.reversedLineOrder}
              text={lineReversed}
            />
          )}
        </div>
      )}
    </>
  );
}
